import React from 'react';
import { executeActionRequest } from '../../../services/trips/actionRequests';
import ActionResolutionView from './ActionResolutionView';
import './chatCards.css';

export interface TripDraftShape {
  title?: string;
  destinations?: string[];
  dates?: { startDate?: string; endDate?: string } | null;
  budget?: { amount?: number; currency?: string } | null;
  description?: string;
}

interface TripDraftCardProps {
  draft: TripDraftShape;
  resolution?: 'confirmed' | 'dismissed' | 'failed';
  createdTripId?: string;
  resolutionNote?: string;
  backTo?: string;
  conversationId?: string;
  // UUID of the trip already created for this chat; when set, block re-creation.
  existingTripId?: string;
  // Safety net: trip_draft has no activities field by schema, so when this card
  // creates the trip, the stops come from the SAME message's structured fields
  // (top-level activities + recommended places — collectFallbackActivities).
  // Without this, a draft-only response saves a trip with zero stops.
  fallbackActivities?: Array<Record<string, any>>;
  onResolve: (resolution: 'confirmed' | 'dismissed' | 'failed', note: string, tripId?: string) => void;
}

const TripDraftCard: React.FC<TripDraftCardProps> = ({
  draft,
  resolution,
  createdTripId,
  resolutionNote,
  backTo = '/ask-ai',
  conversationId,
  existingTripId,
  fallbackActivities,
  onResolve,
}) => {
  const [busy, setBusy] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const handleConfirm = async () => {
    if (busy) return;
    if (!draft.destinations || draft.destinations.length === 0) {
      setValidationError('Add at least one destination before creating the trip.');
      return;
    }
    if (!draft.dates?.startDate || !draft.dates?.endDate) {
      setValidationError('Ask the assistant to set start and end dates first.');
      return;
    }
    setValidationError(null);
    setBusy(true);
    try {
      const result = await executeActionRequest({
        action: 'CREATE_TRIP',
        payload: draft as Record<string, any>,
      }, { conversationId, fallbackActivities });
      onResolve('confirmed', result.note, result.tripId);
    } catch (error: any) {
      onResolve('failed', error?.message || 'The trip could not be created');
    } finally {
      setBusy(false);
    }
  };

  if (resolution) {
    return (
      <ActionResolutionView
        resolution={resolution}
        note={resolution === 'confirmed' ? (resolutionNote || '✓ Trip created') : resolutionNote}
        tripId={createdTripId}
        backTo={backTo}
      />
    );
  }

  // A chat gets at most one trip: block re-creation and link the existing trip.
  if (existingTripId) {
    return (
      <ActionResolutionView
        resolution="confirmed"
        note="This chat already has a trip"
        tripId={existingTripId}
        backTo={backTo}
      />
    );
  }

  return (
    <div className="v2-chat-card v2-chat-card--trip-draft" role="group" aria-label="Confirm trip draft">
      <p className="v2-chat-card__eyebrow">Trip preview</p>
      <p className="v2-chat-card__title">{draft.title || 'Your trip'}</p>
      <p className="v2-chat-card__summary">
        {(draft.destinations || []).join(' · ') || 'No destinations yet'}
        {draft.dates?.startDate && draft.dates?.endDate
          ? ` — ${draft.dates.startDate} → ${draft.dates.endDate}`
          : ' — dates not set'}
        {draft.budget?.amount ? ` — ${draft.budget.amount} ${draft.budget.currency || ''}` : ''}
      </p>
      {validationError && (
        <p className="v2-chat-card__error" role="alert">{validationError}</p>
      )}
      <div className="v2-chat-card__actions">
        <button type="button" className="v2-chat-card__confirm" onClick={handleConfirm} disabled={busy}>
          {busy ? 'Creating…' : 'Create trip'}
        </button>
        <button
          type="button"
          className="v2-chat-card__cancel"
          onClick={() => onResolve('dismissed', 'Dismissed')}
          disabled={busy}
        >
          Not now
        </button>
      </div>
    </div>
  );
};

export default TripDraftCard;
